import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Copy, Check, X } from "lucide-react";

import { SUMMER_SALE } from "@/lib/promo";

const PROMO_CODE = SUMMER_SALE.code;
const PROMO_END = new Date("2026-09-30T23:59:59Z").getTime();
const SEEN_KEY = "summer_sale_modal_seen_v1";
const DELAY_MS = 1500;

const labels: Record<string, { eyebrow: string; title: string; sub: string; bullet1: string; bullet2: string; cta: string; copied: string; close: string }> = {
  fr: {
    eyebrow: "Soldes d'été",
    title: "-25% sur tout le site",
    sub: "Remise appliquée automatiquement au paiement. Offre limitée.",
    bullet1: "Aucune action requise au paiement",
    bullet2: "Valable sur toute la boutique",
    cta: "Copier le code",
    copied: "Code copié !",
    close: "Fermer",
  },
  en: {
    eyebrow: "Summer sale",
    title: "25% off everything",
    sub: "Discount applied automatically at checkout. Limited time.",
    bullet1: "Nothing to enter at checkout",
    bullet2: "Valid storewide",
    cta: "Copy the code",
    copied: "Code copied!",
    close: "Close",
  },
  nl: {
    eyebrow: "Zomersale",
    title: "25% korting op alles",
    sub: "Korting wordt automatisch verrekend bij het afrekenen. Beperkte tijd.",
    bullet1: "Niets in te vullen bij het afrekenen",
    bullet2: "Geldig in de hele winkel",
    cta: "Code kopiëren",
    copied: "Code gekopieerd!",
    close: "Sluiten",
  },
  pl: {
    eyebrow: "Letnia wyprzedaż",
    title: "-25% na wszystko",
    sub: "Rabat naliczany automatycznie przy płatności. Oferta ograniczona.",
    bullet1: "Nic nie trzeba wpisywać przy kasie",
    bullet2: "Ważne w całym sklepie",
    cta: "Skopiuj kod",
    copied: "Kod skopiowany!",
    close: "Zamknij",
  },
  ro: {
    eyebrow: "Reduceri de vară",
    title: "-25% la tot",
    sub: "Reducerea se aplică automat la finalizare. Ofertă limitată.",
    bullet1: "Nu trebuie introdus nimic la plată",
    bullet2: "Valabil pe tot magazinul",
    cta: "Copiază codul",
    copied: "Cod copiat!",
    close: "Închide",
  },
};

export const LaunchPromoModal = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState<string>("fr");

  useEffect(() => {
    if (!SUMMER_SALE.active || Date.now() > PROMO_END) return;
    if (localStorage.getItem(SEEN_KEY) === "1") return;
    try {
      const raw = localStorage.getItem("i18n-storage");
      if (raw) {
        const parsed = JSON.parse(raw);
        const l = parsed?.state?.language;
        if (l && labels[l]) setLang(l);
      }
    } catch {}
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const t = labels[lang] ?? labels.en;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="absolute inset-0 bg-background/80"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="launch-promo-title"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-primary/30 bg-card shadow-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-primary/5" aria-hidden />
            <button
              onClick={close}
              aria-label={t.close}
              className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative p-7 sm:p-8">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                {t.eyebrow}
              </div>

              <h2 id="launch-promo-title" className="font-serif text-3xl sm:text-4xl font-semibold leading-tight text-foreground">
                {t.title}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{t.sub}</p>

              <ul className="mt-5 space-y-2 text-sm text-foreground/90">
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {t.bullet1}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {t.bullet2}
                </li>
              </ul>

              <div className="mt-6 flex items-center gap-2 rounded-xl border border-dashed border-primary/50 bg-primary/5 p-3">
                <span className="flex-1 text-center font-mono text-lg font-bold tracking-[0.25em] text-primary">
                  {PROMO_CODE}
                </span>
                <button
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? t.copied : t.cta}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};